import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient, APIResponse } from '../../../api/client';
import { Ingredient } from '../../../api/queries/inventory.queries';
import { X, Plus, Trash2 } from 'lucide-react';

interface IngredientModalProps {
  ingredient: Ingredient | null;
  ingredients: Ingredient[];
  categories: string[];
  onClose: () => void;
}

interface ComponentInput {
  ingredientId: string;
  quantity: number;
  unit: string;
}

const UNITS = ['ml', 'g', 'pcs', 'kg', 'l'];

function IngredientModal({ ingredient, ingredients, categories, onClose }: IngredientModalProps) {
  const queryClient = useQueryClient();
  const isEditing = !!ingredient;

  const [formData, setFormData] = useState({
    name: ingredient?.name || '',
    sku: ingredient?.sku || '',
    category: ingredient?.category || categories[0],
    unit: ingredient?.unit || 'g',
    minStockLevel: ingredient?.minStockLevel || 0,
    maxStockLevel: ingredient?.maxStockLevel || 0,
    reorderPoint: ingredient?.reorderPoint || 0,
    isComposite: ingredient?.isComposite || false,
    yieldQuantity: ingredient?.yieldQuantity || 1,
    yieldUnit: ingredient?.yieldUnit || 'pcs',
  });

  const [components, setComponents] = useState<ComponentInput[]>(
    ingredient?.components?.map(c => ({
      ingredientId: typeof c.ingredientId === 'object' ? c.ingredientId._id : c.ingredientId,
      quantity: c.quantity,
      unit: c.unit,
    })) || []
  );

  const createMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await apiClient.post<APIResponse<{ ingredient: Ingredient }>>(
        '/inventory/ingredients',
        data
      );
      return response.data.data.ingredient;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ingredients'] });
      onClose();
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await apiClient.put<APIResponse<{ ingredient: Ingredient }>>(
        `/inventory/ingredients/${ingredient?._id}`,
        data
      );
      return response.data.data.ingredient;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ingredients'] });
      onClose();
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const data = {
      ...formData,
      components: formData.isComposite ? components : undefined,
    };

    if (isEditing) {
      updateMutation.mutate(data);
    } else {
      createMutation.mutate(data);
    }
  };

  const addComponent = () => {
    const availableIngredients = ingredients.filter(
      i => !i.isComposite && !components.some(c => c.ingredientId === i._id)
    );
    if (availableIngredients.length > 0) {
      setComponents([
        ...components,
        {
          ingredientId: availableIngredients[0]._id,
          quantity: 1,
          unit: availableIngredients[0].unit,
        },
      ]);
    }
  };

  const removeComponent = (index: number) => {
    setComponents(components.filter((_, i) => i !== index));
  };

  const updateComponent = (index: number, field: keyof ComponentInput, value: any) => {
    const newComponents = [...components];
    newComponents[index] = { ...newComponents[index], [field]: value };

    // Auto-update unit when ingredient changes
    if (field === 'ingredientId') {
      const selectedIngredient = ingredients.find(i => i._id === value);
      if (selectedIngredient) {
        newComponents[index].unit = selectedIngredient.unit;
      }
    }

    setComponents(newComponents);
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;
  const error = createMutation.error || updateMutation.error;

  // Get available raw ingredients for BOM
  const rawIngredients = ingredients.filter(i => !i.isComposite);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-card rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b sticky top-0 bg-card">
          <h2 className="text-lg font-semibold">
            {isEditing ? 'Edit Ingredient' : 'Add New Ingredient'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {error && (
            <div className="p-3 bg-destructive/10 text-destructive rounded-lg text-sm">
              {(error as Error).message}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            {/* Name */}
            <div>
              <label className="block text-sm font-medium mb-1">Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                required
              />
            </div>

            {/* SKU */}
            <div>
              <label className="block text-sm font-medium mb-1">SKU *</label>
              <input
                type="text"
                value={formData.sku}
                onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                required
                placeholder="e.g., RAW-001"
              />
            </div>

            {/* Category */}
            <div>
              <label className="block text-sm font-medium mb-1">Category *</label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
              >
                {categories.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
            </div>

            {/* Unit */}
            <div>
              <label className="block text-sm font-medium mb-1">Unit *</label>
              <select
                value={formData.unit}
                onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
              >
                {UNITS.map((unit) => (
                  <option key={unit} value={unit}>
                    {unit}
                  </option>
                ))}
              </select>
            </div>

            {/* Min Stock Level */}
            <div>
              <label className="block text-sm font-medium mb-1">Min Stock Level</label>
              <input
                type="number"
                value={formData.minStockLevel}
                onChange={(e) => setFormData({ ...formData, minStockLevel: Number(e.target.value) })}
                className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                min="0"
              />
            </div>

            {/* Max Stock Level */}
            <div>
              <label className="block text-sm font-medium mb-1">Max Stock Level</label>
              <input
                type="number"
                value={formData.maxStockLevel}
                onChange={(e) => setFormData({ ...formData, maxStockLevel: Number(e.target.value) })}
                className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                min="0"
              />
            </div>
          </div>

          {/* Composite Toggle */}
          <div className="border-t pt-4">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.isComposite}
                onChange={(e) => setFormData({ ...formData, isComposite: e.target.checked })}
                className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
              />
              <div>
                <span className="font-medium">Composite Ingredient (BOM)</span>
                <p className="text-sm text-muted-foreground">
                  This ingredient is made from other raw materials
                </p>
              </div>
            </label>
          </div>

          {/* BOM Components */}
          {formData.isComposite && (
            <div className="border rounded-lg p-4 space-y-4 bg-muted/20">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium">Bill of Materials</h3>
                  <p className="text-sm text-muted-foreground">
                    Define the raw materials needed to make this ingredient
                  </p>
                </div>
                <button
                  type="button"
                  onClick={addComponent}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
                  disabled={rawIngredients.length === components.length}
                >
                  <Plus className="h-4 w-4" />
                  Add Component
                </button>
              </div>

              {/* Yield */}
              <div className="grid grid-cols-2 gap-4 pb-4 border-b">
                <div>
                  <label className="block text-sm font-medium mb-1">Yield Quantity</label>
                  <input
                    type="number"
                    value={formData.yieldQuantity}
                    onChange={(e) => setFormData({ ...formData, yieldQuantity: Number(e.target.value) })}
                    className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                    min="1"
                    step="0.01"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Yield Unit</label>
                  <select
                    value={formData.yieldUnit}
                    onChange={(e) => setFormData({ ...formData, yieldUnit: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    {UNITS.map((unit) => (
                      <option key={unit} value={unit}>
                        {unit}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Components List */}
              {components.length === 0 ? (
                <div className="text-center py-4 text-muted-foreground">
                  No components added yet
                </div>
              ) : (
                <div className="space-y-3">
                  {components.map((component, index) => (
                    <div key={index} className="flex items-center gap-3 bg-background p-3 rounded-lg border">
                      <div className="flex-1">
                        <select
                          value={component.ingredientId}
                          onChange={(e) => updateComponent(index, 'ingredientId', e.target.value)}
                          className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                        >
                          {rawIngredients.map((ing) => (
                            <option
                              key={ing._id}
                              value={ing._id}
                              disabled={components.some(c => c.ingredientId === ing._id && c.ingredientId !== component.ingredientId)}
                            >
                              {ing.name} ({ing.sku})
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="w-24">
                        <input
                          type="number"
                          value={component.quantity}
                          onChange={(e) => updateComponent(index, 'quantity', Number(e.target.value))}
                          className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                          min="0.01"
                          step="0.01"
                        />
                      </div>
                      <div className="w-20">
                        <select
                          value={component.unit}
                          onChange={(e) => updateComponent(index, 'unit', e.target.value)}
                          className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                        >
                          {UNITS.map((unit) => (
                            <option key={unit} value={unit}>
                              {unit}
                            </option>
                          ))}
                        </select>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeComponent(index)}
                        className="p-2 hover:bg-destructive/10 text-destructive rounded-lg transition-colors"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border rounded-lg hover:bg-muted transition-colors"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50"
              disabled={isLoading}
            >
              {isLoading ? 'Saving...' : isEditing ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default IngredientModal;
